import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "@material-ui/core";
import api from "../../services/api.js";
import {
  getAttributeInputType,
  parseDailyHoursValue,
  serializeDailyHoursValue,
} from "./utils";

const fieldStyle = {
  width: "100%",
  height: 36,
  padding: "8px 10px",
  border: "1px solid #D1D5DB",
  borderRadius: 8,
  fontSize: 13,
  color: "#111827",
  boxSizing: "border-box",
};

export default function ModalEditUserStoryAttributes({
  isOpen,
  onClose,
  story,
  onSuccess,
}) {
  const [attributes, setAttributes] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const storyId = story?.id;

  useEffect(() => {
    if (!isOpen || !storyId) return;

    const loadAttributes = async () => {
      try {
        setLoading(true);
        setError("");
        const { data } = await api.get("/userStoryAttributeValues", {
          params: { userStoryId: storyId },
        });

        const rows = Array.isArray(data) ? data : [];
        const nextAttributes = rows.map((row) => ({
          id: row.id_atributo,
          name: row.nome_atributo,
          value: row.valor_atributo ?? "",
          inputType: getAttributeInputType(row.nome_atributo),
        }));

        const initialValues = nextAttributes.reduce((acc, attr) => {
          if (getAttributeInputType(attr.name) === "daily-hours") {
            acc[attr.id] = parseDailyHoursValue(attr.value);
          } else {
            acc[attr.id] = attr.value ?? "";
          }
          return acc;
        }, {});

        setAttributes(nextAttributes);
        setFormValues(initialValues);
      } catch (err) {
        console.error("Erro ao buscar atributos da história:", err);
        setError("Não foi possível carregar os atributos customizados desta história.");
      } finally {
        setLoading(false);
      }
    };

    loadAttributes();
  }, [isOpen, storyId]);

  const handleFieldChange = (attributeId, value) => {
    setFormValues((prev) => ({ ...prev, [attributeId]: value }));
  };

  const handleDailyHoursChange = (attributeId, index, field, value) => {
    setFormValues((prev) => {
      const currentEntries = Array.isArray(prev[attributeId]) ? prev[attributeId] : [];
      const nextEntries = [...currentEntries];
      nextEntries[index] = { ...(nextEntries[index] || {}), [field]: value };
      return { ...prev, [attributeId]: nextEntries };
    });
  };

  const addDailyHoursEntry = (attributeId) => {
    setFormValues((prev) => ({
      ...prev,
      [attributeId]: [
        ...(Array.isArray(prev[attributeId]) ? prev[attributeId] : []),
        { date: "", hours: "" },
      ],
    }));
  };

  const removeDailyHoursEntry = (attributeId, index) => {
    setFormValues((prev) => {
      const currentEntries = Array.isArray(prev[attributeId]) ? prev[attributeId] : [];
      const nextEntries = currentEntries.filter((_, entryIndex) => entryIndex !== index);
      return { ...prev, [attributeId]: nextEntries };
    });
  };

  const serializeValue = (attribute, rawValue) => {
    const inputType = getAttributeInputType(attribute.name);

    if (inputType === "daily-hours") {
      return serializeDailyHoursValue(rawValue || {});
    }

    if (inputType === "number") {
      return rawValue === "" || rawValue === null || rawValue === undefined
        ? ""
        : String(rawValue);
    }

    if (inputType === "date") {
      return rawValue || "";
    }

    return rawValue ?? "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    try {
      setSaving(true);
      const attributeValues = attributes.reduce((acc, attribute) => {
        acc[String(attribute.id)] = serializeValue(attribute, formValues[attribute.id]);
        return acc;
      }, {});

      await api.patch("/editUserStoryAttribute", {
        userStoryId: storyId,
        attributeValues,
      });

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      console.error("Erro ao atualizar atributos da história:", err);
      setError(err.response?.data?.error || "Erro ao salvar os atributos customizados.");
    } finally {
      setSaving(false);
    }
  };

  const title = useMemo(() => {
    if (!story?.subject && !story?.name) return "Editar atributos da história";
    return `Editar atributos de ${story.subject || story.name}`;
  }, [story]);

  if (!isOpen) return null;

  return (
    <Modal open={isOpen} onClose={onClose}>
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 23, 42, 0.55)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
          zIndex: 1100,
        }}
        onClick={onClose}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 760,
            maxHeight: "90vh",
            overflowY: "auto",
            background: "#FFFFFF",
            borderRadius: 12,
            padding: 24,
            boxShadow: "0 20px 45px rgba(0,0,0,0.2)",
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
              gap: 12,
            }}
          >
            <div>
              <h2 style={{ margin: 0, fontSize: 18, color: "#111827" }}>{title}</h2>
              <div style={{ marginTop: 4, fontSize: 13, color: "#6B7280" }}>
                Atualize os valores customizados desta história de usuário.
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              style={{
                border: "none",
                background: "transparent",
                fontSize: 22,
                color: "#6B7280",
                cursor: "pointer",
              }}
            >
              ×
            </button>
          </div>

          {error && (
            <div
              style={{
                background: "#FEE2E2",
                color: "#991B1B",
                borderRadius: 8,
                padding: "10px 12px",
                marginBottom: 16,
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}

          {loading ? (
            <div style={{ padding: "20px 0", color: "#6B7280" }}>Carregando atributos...</div>
          ) : attributes.length === 0 ? (
            <div style={{ padding: "20px 0", color: "#6B7280" }}>
              Nenhum atributo customizado encontrado para esta história.
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {attributes.map((attribute) => {
                const inputType = getAttributeInputType(attribute.name);

                return (
                  <div key={attribute.id} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
                      {attribute.name}
                    </label>

                    {inputType === "daily-hours" ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {(Array.isArray(formValues[attribute.id]) ? formValues[attribute.id] : []).map((entry, index) => (
                          <div
                            key={`${attribute.id}-${index}`}
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1.2fr 0.8fr auto",
                              gridTemplateRows: "auto auto",
                              columnGap: 8,
                              rowGap: 4,
                              alignItems: "start",
                            }}
                          >
                            <label style={{ gridColumn: 1, gridRow: 1, fontSize: 12, color: "#6B7280" }}>
                              Data
                            </label>
                            <label style={{ gridColumn: 2, gridRow: 1, fontSize: 12, color: "#6B7280" }}>
                              Horas
                            </label>
                            <div style={{ gridColumn: 3, gridRow: 1 }} />

                            <input
                              type="text"
                              value={entry?.date ?? ""}
                              onChange={(event) => handleDailyHoursChange(attribute.id, index, "date", event.target.value)}
                              style={{ ...fieldStyle, gridColumn: 1, gridRow: 2 }}
                              placeholder="22/04/2025"
                            />
                            <input
                              type="number"
                              min="0"
                              step="0.5"
                              value={entry?.hours ?? ""}
                              onChange={(event) => handleDailyHoursChange(attribute.id, index, "hours", event.target.value)}
                              style={{ ...fieldStyle, gridColumn: 2, gridRow: 2 }}
                              placeholder="8"
                            />
                            <button
                              type="button"
                              onClick={() => removeDailyHoursEntry(attribute.id, index)}
                              style={{
                                gridColumn: 3,
                                gridRow: 2,
                                height: 36,
                                padding: "0 12px",
                                border: "1px solid #D1D5DB",
                                borderRadius: 8,
                                background: "#FFFFFF",
                                color: "#374151",
                                cursor: "pointer",
                                fontSize: 13,
                                lineHeight: "34px",
                                boxSizing: "border-box",
                              }}
                            >
                              Remover
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => addDailyHoursEntry(attribute.id)}
                          style={{
                            padding: "8px 12px",
                            border: "1px solid #D1D5DB",
                            borderRadius: 8,
                            background: "#FFFFFF",
                            color: "#374151",
                            cursor: "pointer",
                            fontSize: 13,
                            fontWeight: 600,
                            alignSelf: "flex-start",
                          }}
                        >
                          + Adicionar data
                        </button>
                      </div>
                    ) : inputType === "number" ? (
                      <input
                        type="number"
                        step="0.5"
                        value={formValues[attribute.id] ?? ""}
                        onChange={(event) => handleFieldChange(attribute.id, event.target.value)}
                        style={fieldStyle}
                      />
                    ) : inputType === "date" ? (
                      <input
                        type="date"
                        value={formValues[attribute.id] ?? ""}
                        onChange={(event) => handleFieldChange(attribute.id, event.target.value)}
                        style={fieldStyle}
                      />
                    ) : (
                      <input
                        type="text"
                        value={formValues[attribute.id] ?? ""}
                        onChange={(event) => handleFieldChange(attribute.id, event.target.value)}
                        style={fieldStyle}
                      />
                    )}
                  </div>
                );
              })}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 8 }}>
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    padding: "8px 16px",
                    border: "1px solid #D1D5DB",
                    borderRadius: 8,
                    background: "#FFFFFF",
                    color: "#374151",
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    padding: "8px 16px",
                    border: "none",
                    borderRadius: 8,
                    background: saving ? "#9CA3AF" : "#1D4ED8",
                    color: "#FFFFFF",
                    cursor: saving ? "not-allowed" : "pointer",
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  {saving ? "Salvando..." : "Salvar alterações"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </Modal>
  );
}